import { useEffect, useState } from "react";
import { useSession } from "@/lib/auth-client";
import { useIsOnline } from "@/lib/hooks/use-is-online";

/**
 * Offline session fallback.
 *
 * better-auth resolves the session with a network call to `/get-session` on
 * every load, and the session cookie is httpOnly so client JS can't read it
 * directly. So a cold start with no network has no way to know the user is
 * logged in and `AuthGate` bounces to `/login` — even though a valid session
 * cookie is sitting right there and the local PowerSync replica is full of
 * their data.
 *
 * This caches a MINIMAL identity (user id + active property id — never the
 * token, never PII) on each successful resolution and replays it when the
 * network can't be reached, so a previously-logged-in device opens straight
 * into the app and renders from local data. The server stays authoritative:
 * the moment the device is back online a real `/get-session` either refreshes
 * the identity or, if it reports no session (sign-out / expiry), clears it.
 *
 * The cache is honored for at most `MAX_AGE_MS`, mirroring better-auth's
 * default session lifetime (`expiresIn`: 7 days) so an offline identity can't
 * outlive the server session it stands in for.
 */

const STORAGE_KEY = "superhost.session.identity";
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

/** The minimal session shape the app gates and scopes on. */
export interface AppSession {
  session: { activeOrganizationId: string | null };
  user: { id: string };
}

type CachedIdentity = AppSession & { cachedAt: number };

function readCache(): CachedIdentity | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as CachedIdentity;
    if (!parsed?.user?.id || typeof parsed.cachedAt !== "number") {
      return null;
    }
    if (Date.now() - parsed.cachedAt > MAX_AGE_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function writeCache(session: AppSession): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    const identity: CachedIdentity = {
      cachedAt: Date.now(),
      session: { activeOrganizationId: session.session.activeOrganizationId },
      user: { id: session.user.id },
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(identity));
  } catch {
    // localStorage unavailable (private mode / quota) — degrade to no cache.
  }
}

function clearCache(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Drop the cached identity now, rather than when the next `/get-session`
 * reports no session. A sign-out that reloads the page has to: the reload
 * lands before that refetch, and would otherwise boot straight back into the
 * app as the account that just left.
 */
export function forgetCachedIdentity(): void {
  clearCache();
}

/**
 * The cached identity as it stood when this page booted, before any sign-in on
 * the page could overwrite it: the account that last had a session here.
 * device-replica.ts reads it to recognise a replica left by a build that did
 * not record its owner.
 */
const bootIdentityUserId: string | null = readCache()?.user.id ?? null;

export function readBootIdentityUserId(): string | null {
  return bootIdentityUserId;
}

/**
 * Drop-in replacement for `useSession` for the components that gate or scope on
 * identity (`AuthGate`, `DefaultAppRedirect`, `AppProvider`, the global
 * filter). Returns the live session when the network resolves it, and falls
 * back to the cached identity while offline so the app keeps rendering instead
 * of bouncing to `/login`.
 */
export function useAppSession(): {
  data: AppSession | null;
  isPending: boolean;
  error: unknown;
  /** True while the identity is being served from cache (network unreachable). */
  isOffline: boolean;
} {
  const live = useSession();
  const isOnline = useIsOnline();
  const [cached, setCached] = useState<CachedIdentity | null>(readCache);

  const liveUserId = live.data?.user?.id ?? null;
  // The organization plugin's `activeOrganizationId` lives on `session.session`
  // but doesn't survive better-auth's type inference (see auth-client.ts), so
  // it's read through a narrow cast — same pattern the consumers used before.
  const liveActiveOrg =
    (live.data as { session?: { activeOrganizationId?: string | null } } | null)
      ?.session?.activeOrganizationId ?? null;

  useEffect(() => {
    if (live.isPending) {
      return;
    }
    if (liveUserId) {
      writeCache({
        session: { activeOrganizationId: liveActiveOrg },
        user: { id: liveUserId },
      });
      setCached(readCache());
      return;
    }
    // Resolved with no session while the server was reachable (online, no fetch
    // error) → a genuine sign-out or expiry. Drop the cached identity so it
    // can't resurrect the user offline later.
    if (!live.error && isOnline) {
      clearCache();
      setCached(null);
    }
  }, [live.isPending, liveUserId, liveActiveOrg, live.error, isOnline]);

  // "Definitively logged out" needs the network to have spoken: resolved, empty,
  // no error, online. Anything else (pending, fetch error, offline) keeps the
  // cached identity in play rather than ejecting the user.
  const loggedOut = !(live.isPending || liveUserId || live.error) && isOnline;

  let data: AppSession | null;
  if (liveUserId) {
    data = {
      session: { activeOrganizationId: liveActiveOrg },
      user: { id: liveUserId },
    };
  } else if (loggedOut) {
    data = null;
  } else {
    data = cached;
  }

  return {
    data,
    error: live.error,
    isOffline: !(liveUserId || loggedOut) && cached !== null,
    // With a cached identity we never strand the UI on the network probe.
    isPending: live.isPending && cached === null,
  };
}
